// import { useXXXModal } from '../new-modal';

import _ from 'lodash';
import { useNewAddressModal } from '../screens/location/new-address-modal';
import { useAddressComplementModal } from '../screens/location/address-complement-modal';
import { useAddressMapModal } from '../screens/location/address-map-modal';
import { useAddressImcompleteModal } from '../screens/location/address-imcomplete-modal';
import { useAddressCurrentLocationModal } from '../screens/location/address-current-location-modal';
import {
  parseMapPostalCode,
  parseMapRoute,
  parseMapStreetNumber,
} from '../utils/manager/map-manager';
import { useAddressStreetNumberModal } from '../screens/location/address-street-number-modal';
import { useStorage } from '../hooks/use-storage';
import { useAccountAddressesAPI } from '../apis/account-address-api';

function useNewAddressWorkflow() {
  const { userRef } = useStorage();
  const { clearNewAddressModal, showNewAddressModal } = useNewAddressModal();
  const { clearAddressComplementModal, showAddressComplementModal } =
    useAddressComplementModal();
  const { clearAddressMapModal, showAddressMapModal } = useAddressMapModal();
  const { showAddressImcompleteModal } = useAddressImcompleteModal();
  const { showAddressCurrentLocationModal } = useAddressCurrentLocationModal();
  const { clearAddressStreetNumberModal, showAddressStreetNumberModal } =
    useAddressStreetNumberModal();
  const { insertAccountAddressFromRemote } = useAccountAddressesAPI();

  async function clear() {
    await clearNewAddressModal();
  }

  async function show({
    showBackButton,
    avoidAnimation,
    prepareOnPreConfirm,
  } = {}) {
    let repeat = false;
    let result = {};
    let previousHasStreetNumber = null;

    /* eslint-disable no-await-in-loop */
    do {
      if (
        result.wasImcompleteBackButtonClicked ||
        result.wasStreetNumberBackButtonClicked ||
        (result.wasMapBackButtonClicked && result.wasMapMarkerInvalid) ||
        (!result.wasMapBackButtonClicked &&
          !result.wasComplementBackButtonClicked) ||
        (result.wasComplementBackButtonClicked &&
          previousHasStreetNumber === true)
      ) {
        const showResult = await showNewAddressModal({
          showBackButton,
          avoidAnimation,
          wasBackButtonClicked: result.isDismissed,
        });

        result.wasImcompleteBackButtonClicked = false;
        result.wasStreetNumberBackButtonClicked = false;
        result.wasComplementBackButtonClicked = false;
        result.wasCurrentLocatiotBackButtonClicked = false;
        result.wasMapBackButtonClicked = false;

        previousHasStreetNumber = null;

        delete result.placeDetails;
        delete result.baseLocation;

        result = _.merge(result, showResult);
      }

      if (result.isConfirmed && result.wasCurrentLocationButtonClicked) {
        const showResult = await showAddressCurrentLocationModal();

        result.wasCurrentLocationButtonClicked = false;
        previousHasStreetNumber = null;

        delete result.placeDetails;
        delete result.baseLocation;

        result = _.merge(result, showResult);
      }

      const hasRoute = !_.isEmpty(parseMapRoute(result.placeDetails));
      const hasPostalCode = !_.isEmpty(parseMapPostalCode(result.placeDetails));

      if (result.isConfirmed && (!hasRoute || !hasPostalCode)) {
        const showResult = await showAddressImcompleteModal({
          placeDetails: result.placeDetails,
          baseLocation: result.baseLocation,
        });

        result = _.merge(result, showResult);
      }

      const hasStreetNumber = !_.isEmpty(
        parseMapStreetNumber(result.placeDetails)
      );

      if (
        (result.isConfirmed && !hasStreetNumber) ||
        (result.wasComplementBackButtonClicked &&
          previousHasStreetNumber === false)
      ) {
        if (result.isConfirmed) await clearAddressStreetNumberModal();

        const showResult = await showAddressStreetNumberModal({
          placeDetails: result.placeDetails,
          baseLocation: result.baseLocation,
        });

        result.wasComplementBackButtonClicked = false;

        result = _.merge(result, showResult);
      }

      if (previousHasStreetNumber == null) {
        previousHasStreetNumber = hasStreetNumber;
      }

      if (
        (result.isConfirmed && result.placeDetails != null) ||
        result.wasMapBackButtonClicked
      ) {
        if (result.isConfirmed) await clearAddressComplementModal();

        const showResult = await showAddressComplementModal({
          placeDetails: result.placeDetails,
          baseLocation: result.baseLocation,
          noStreetNumber: result.noStreetNumber,
        });

        result.wasMapBackButtonClicked = false;

        result = _.merge(result, showResult);
      }

      if (result.isConfirmed && result.placeDetails != null) {
        await clearAddressMapModal();

        const { placeDetails, baseLocation, noStreetNumber, complement } =
          result;

        let address = null;

        const showResult = await showAddressMapModal({
          placeDetails,
          baseLocation,
          noStreetNumber,
          complement,
          prepareOnPreConfirm: async (center) => {
            address = await saveAddress({
              placeDetails,
              baseLocation,
              noStreetNumber,
              complement,
              center,
            });
            await prepareOnPreConfirm?.call(this);
          },
        });

        if (showResult.isConfirmed) {
          showResult.address = address;
        }

        result = _.merge(result, showResult);
      }

      repeat =
        result.wasCurrentLocatiotBackButtonClicked ||
        result.wasImcompleteBackButtonClicked ||
        result.wasStreetNumberBackButtonClicked ||
        result.wasComplementBackButtonClicked ||
        result.wasMapBackButtonClicked;
    } while (repeat);

    if (result.isConfirmed) {
      // TODO: Implement this
    }

    return result;
  }

  async function saveAddress({
    placeDetails,
    baseLocation,
    noStreetNumber,
    complement,
    center,
  }) {
    console.log('placeDetails', placeDetails);
    console.log('baseLocation', baseLocation);
    console.log('noStreetNumber', noStreetNumber);
    console.log('complement', complement);
    console.log('center', center);
    console.log('user', userRef.current);

    const data = {
      country: {
        id: baseLocation.country?.id,
      },
      city: {
        id: baseLocation.city?.id,
      },
      route: parseMapRoute(placeDetails),
      is_no_number: noStreetNumber ?? false,
      number: parseMapStreetNumber(placeDetails),
      complement: _.isEmpty(complement) ? null : complement,
      postal_code: parseMapPostalCode(placeDetails),
      coordinate: {
        latitude: _.isFunction(center.lat) ? center.lat() : center.lat,
        longitude: _.isFunction(center.lng) ? center.lng() : center.lng,
      },
      google_place_id: placeDetails.place_id,
    };

    if (baseLocation.state?.id != null) {
      data.state = {
        id: baseLocation.state.id,
      };
    }

    if (baseLocation.neighborhood?.id != null) {
      data.neighborhood = {
        id: baseLocation.neighborhood.id,
      };
    }

    const response = await insertAccountAddressFromRemote(data);
    return response.data;
  }

  return {
    clearNewAddressWorkflow: clear,
    showNewAddressWorkflow: show,
  };
}

export { useNewAddressWorkflow };
